import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  Avatar,
  Stack,
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Logout } from '@mui/icons-material';
import React, { useMemo } from 'react';
import { NotificationBell } from '~/components/notifications';
import { useCurrentUserProfile } from '~/hooks/useCurrentUserProfile';
import { logoutApp } from '~/lib/auth/logout';
import {
  buildPermissionSet,
  CN01_PLANTER_DASHBOARD_PERMISSIONS,
  hasAnyPermission,
} from '~/lib/permissions';

function formatRegionLabel(profile: { regional_code?: string | null } | null | undefined): string {
  if (!profile) return '—';
  const code = profile.regional_code?.trim();
  if (code) return code;
  return 'Global';
}

function getInitials(name: string) {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'U'
  );
}

export function AppBar({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = React.useState(false);
  const { data: userProfile } = useCurrentUserProfile();

  const jobRoleLabel =
    userProfile?.user_role?.name || userProfile?.jobTitle || userProfile?.role || '—';

  const displayName = useMemo(() => {
    if (!userProfile) return '';
    const derivedName =
      userProfile.fullName ||
      [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ') ||
      userProfile.username;
    return derivedName || 'Unnamed User';
  }, [userProfile]);

  const regionLabel = useMemo(() => formatRegionLabel(userProfile), [userProfile]);

  const isPlanter = useMemo(() => {
    const permissionSet = buildPermissionSet(userProfile?.permissions);
    return hasAnyPermission(permissionSet, CN01_PLANTER_DASHBOARD_PERMISSIONS);
  }, [userProfile?.permissions]);

  const showNotifications = !isPlanter;

  const profileDetailRows = useMemo(() => {
    if (!userProfile) return [];
    return [
      { label: 'Username', value: userProfile.username || '—' },
      { label: 'Email', value: userProfile.email || '—' },
      { label: 'Phone', value: userProfile.phone || '—' },
      { label: 'Role', value: userProfile.user_role?.name || '—' },
      { label: 'Region', value: regionLabel },
    ];
  }, [userProfile, regionLabel]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenProfileDialog = () => {
    handleMenuClose();
    setProfileDialogOpen(true);
  };

  const handleCloseProfileDialog = () => {
    setProfileDialogOpen(false);
  };

  const handleRequestLogout = () => {
    handleMenuClose();
    setLogoutConfirmOpen(true);
  };

  const handleCancelLogout = () => {
    setLogoutConfirmOpen(false);
  };

  const handleConfirmLogout = () => {
    setLogoutConfirmOpen(false);
    logoutApp();
  };

  return (
    <MuiAppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
          {!isPlanter ? (
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={onToggleSidebar}>
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box
            component="img"
            src="/img/SL.png"
            alt="Logo"
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.9)',
              p: 0.5,
              objectFit: 'contain',
              flex: '0 0 auto',
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            Tea Nursery Customer Portal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, minWidth: 0 }}>
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: 'inherit',
              opacity: 0.92,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: 140, sm: 280, md: 400 },
            }}
          >
            {jobRoleLabel} | {regionLabel}
          </Typography>
          {showNotifications ? <NotificationBell enabled={showNotifications} /> : null}
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            aria-label="Account menu"
            aria-controls={anchorEl ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
          >
            <AccountCircle />
          </IconButton>
        </Box>
      </Toolbar>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleOpenProfileDialog}>
          <AccountCircle sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleRequestLogout}>
          <Logout sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      <Dialog open={profileDialogOpen} onClose={handleCloseProfileDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Profile</DialogTitle>
        <DialogContent dividers>
          {userProfile ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56 }}>{getInitials(displayName)}</Avatar>
                <Box minWidth={0}>
                  <Typography variant="h6" noWrap>
                    {displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {userProfile.email || 'No email provided'}
                  </Typography>
                </Box>
              </Stack>
              <Divider />
              <Stack spacing={1.5}>
                {profileDetailRows.map((row) => (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                    key={row.label}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ textAlign: 'right', wordBreak: 'break-word' }}
                    >
                      {row.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No profile data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={logoutConfirmOpen}
        onClose={handleCancelLogout}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Sign out?</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out of the Tea Nursery Customer Portal?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout}>Cancel</Button>
          <Button onClick={handleConfirmLogout} color="error" variant="contained" autoFocus>
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </MuiAppBar>
  );
}
